import { useState } from 'react';
import {
  Button,
  CheckRow,
  DepthGradient,
  EvidenceDrawer,
  RenewalTimeline,
  Screen,
  ScreenHeader,
  SegmentedControl,
  TierBadge,
  QualState,
  WeakestLinkBanner,
  humanize,
  type CheckDetail,
} from '@garnet/ui';
import { StatePill } from '../components/StatePill.js';
import type { CheckStateVM, HeroCheck, HeroRecord, Supplier, VerticalData } from '../data/types.js';
import type { AssuranceCounts } from '../data/shape.js';
import {
  CATEGORY_ORDER,
  assuranceCounts,
  categoryOf,
  categoryState,
  depthLabel,
  toDepth,
} from '../data/shape.js';
import type { Route } from '../nav.js';

type MastheadVariant = 'banner' | 'instrument' | 'split';
type DisclosureTier = 'public' | 'restricted' | 'authority';

/* short coverage labels (prototype SHORT map) */
const SHORT: Record<CheckStateVM, string> = {
  machine: 'verified',
  attestation: 'attested',
  flag: 'flag',
  unverified: 'unverified',
  pending: 'pending',
  error: 'error',
};

/* ── diligence coverage grid ─────────────────────────────── */
function Coverage({ checks }: { checks: HeroCheck[] }) {
  return (
    <div className="panel" style={{ padding: '16px 18px' }}>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        Diligence coverage · {CATEGORY_ORDER.length} categories
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(184px,1fr))',
          gap: 10,
          marginTop: 12,
        }}
      >
        {CATEGORY_ORDER.map((cat) => {
          const cs = checks.filter((c) => categoryOf(c) === cat);
          if (!cs.length) return null;
          const st = categoryState(cs);
          return (
            <div
              key={cat}
              style={{
                border: '1px solid var(--rule)',
                borderRadius: 'var(--radius-md)',
                padding: '11px 12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.3 }}>{cat}</span>
                <StatePill state={st} label={SHORT[st]} />
              </div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  color: 'var(--muted)',
                  marginTop: 6,
                }}
              >
                {cs.length} checks
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── per-check table ─────────────────────────────────────── */
function CheckTable({ checks, tier }: { checks: HeroCheck[]; tier: DisclosureTier }) {
  const [openId, setOpenId] = useState<string | null>('c6');

  function detailsFor(c: HeroCheck): CheckDetail[] {
    const base: CheckDetail[] = [
      { term: 'layer', value: c.layer },
      { term: 'depth', value: depthLabel(c.depth) },
    ];
    if (c.scope) base.push({ term: 'scope', value: c.scope });
    Object.entries(c.match)
      .slice(0, tier === 'public' ? 0 : 4)
      .forEach(([k, v]) => base.push({ term: humanize(k), value: String(v) }));
    return base;
  }

  function drawerFor(c: HeroCheck) {
    if (tier === 'public') {
      return (
        <EvidenceDrawer kind="evidence" meta="public tier">
          <span style={{ color: 'var(--muted)' }}>
            redacted · evidence references available to deal counterparties (restricted) and granted
            authorities
          </span>
        </EvidenceDrawer>
      );
    }
    return (
      <EvidenceDrawer
        kind={'evidence · ' + (tier === 'authority' ? 'full payload' : 'reference')}
        meta={tier === 'authority' ? 'vault ' + c.id + '·a10' : 'hash ' + c.id + '9f…a10'}
      >
        {c.ev}
      </EvidenceDrawer>
    );
  }

  return (
    <div className="panel">
      <div
        className="cmain"
        style={{ cursor: 'default', color: 'var(--muted)', borderBottom: '1px solid var(--ink)' }}
      >
        <span
          className="cid"
          style={{ fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase' }}
        >
          check
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          name
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          source
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          state
        </span>
        <span />
      </div>
      {CATEGORY_ORDER.map((cat) => {
        const cs = checks.filter((c) => categoryOf(c) === cat);
        if (!cs.length) return null;
        return (
          <div key={cat} style={{ display: 'contents' }}>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                fontWeight: 600,
                padding: '8px 14px',
                background: 'var(--rule-soft)',
                borderBottom: '1px solid var(--rule)',
              }}
            >
              {cat}
            </div>
            {cs.map((c) => (
              <CheckRow
                key={c.id}
                id={c.id}
                name={c.name}
                sub={c.sub}
                source={tier === 'public' ? '—' : c.source}
                status={<StatePill state={c.state} />}
                details={detailsFor(c)}
                open={openId === c.id}
                onToggle={() => setOpenId((o) => (o === c.id ? null : c.id))}
              >
                {drawerFor(c)}
              </CheckRow>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ── masthead variants ───────────────────────────────────── */
function MetaItem({ k, v }: { k: string; v: string | number }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {k}
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 13, marginTop: 3 }}>{v}</div>
    </div>
  );
}

interface MastheadProps {
  s: Supplier;
  h: HeroRecord;
  c: AssuranceCounts;
}

function MastheadBanner({ s, h, c }: MastheadProps) {
  return (
    <div className="panel" style={{ padding: '22px 24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', letterSpacing: '.06em' }}>
            {s.id} · {s.role}
          </div>
          <h1
            className="display"
            style={{
              fontSize: '2rem',
              fontWeight: 700,
              letterSpacing: '-.02em',
              margin: '5px 0 0',
              lineHeight: 1.04,
            }}
          >
            {s.name}
          </h1>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>
            {s.flag} {s.place} · {s.register} {s.registerId}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 9 }}>
          <QualState state={s.state} />
          <TierBadge level={s.tier} />
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))',
          gap: 18,
          marginTop: 20,
          paddingTop: 18,
          borderTop: '1px solid var(--rule)',
        }}
      >
        <MetaItem k="machine coverage" v={s.machineCoverage + '%'} />
        <MetaItem
          k="checks"
          v={c.machine + c.attestation + ' of ' + (c.machine + c.attestation + c.flag + c.unverified)}
        />
        <MetaItem k="open cases" v={s.openCases} />
        <MetaItem k="record version" v={'v' + h.version} />
        <MetaItem k="issued" v={h.issued.split(' ')[0] ?? h.issued} />
      </div>
    </div>
  );
}

function MastheadInstrument({ s, h }: MastheadProps) {
  const rows: Array<[string, string]> = [
    ['supplier', s.name],
    ['counterparty id', s.id],
    ['role in deal', s.role],
    ['jurisdiction', s.flag + ' ' + s.jurisdiction + ' · ' + s.place],
    ['register', s.register + ' ' + s.registerId],
    ['asset under claim', s.asset],
    ['consignment', s.consignment],
    ['record', h.recordId + ' · supersedes ' + h.supersedes],
    ['rule engine', h.ruleVersion],
  ];
  return (
    <div className="panel">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          padding: '16px 20px',
          borderBottom: '1px solid var(--ink)',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
          }}
        >
          Qualification record
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <QualState state={s.state} />
          <TierBadge level={s.tier} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {rows.map(([k, v], i) => (
          <div
            key={k}
            style={{
              display: 'grid',
              gridTemplateColumns: '150px 1fr',
              gap: 12,
              padding: '11px 20px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--rule)' : 0,
              borderRight: i % 2 === 0 ? '1px solid var(--rule)' : 0,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              {k}
            </span>
            <span
              style={{
                fontFamily: i === 0 ? 'var(--display)' : 'var(--mono)',
                fontSize: i === 0 ? 16 : 12.5,
                fontWeight: i === 0 ? 600 : 400,
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MastheadSplit({ s, h, c }: MastheadProps) {
  const rows: Array<[string, number, 'm' | 'a' | 'f' | 'n']> = [
    ['machine', c.machine, 'm'],
    ['attestation', c.attestation, 'a'],
    ['flag', c.flag, 'f'],
    ['unverified', c.unverified, 'n'],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, alignItems: 'stretch' }}>
      <div
        className="panel"
        style={{
          padding: '22px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', letterSpacing: '.06em' }}>
            {s.id} · {s.role}
          </div>
          <h1
            className="display"
            style={{
              fontSize: '2rem',
              fontWeight: 700,
              letterSpacing: '-.02em',
              margin: '5px 0 10px',
              lineHeight: 1.04,
            }}
          >
            {s.name}
          </h1>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--muted)' }}>
            {s.flag} {s.place} · {s.register} {s.registerId}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <QualState state={s.state} />
          <TierBadge level={s.tier} />
        </div>
      </div>
      <div className="panel">
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            padding: '13px 16px',
            borderBottom: '1px solid var(--ink)',
          }}
        >
          Assurance ledger · {h.checks.length} checks
        </div>
        {rows.map(([k, v, cl], i) => (
          <div
            key={k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '11px 16px',
              borderBottom: i < 3 ? '1px solid var(--rule)' : 0,
            }}
          >
            <span className={'pill ' + cl} style={{ fontSize: 10 }}>
              {k}
            </span>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 18,
                fontWeight: 600,
                color: v ? 'var(--ink)' : 'var(--muted)',
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SupplierRecord({ data, go }: { data: VerticalData; go: (r: Route) => void }) {
  const { hero } = data;
  const s = data.suppliers.find((x) => x.id === hero.supplierId) as Supplier;
  const c = assuranceCounts(hero.checks);
  const [variant, setVariant] = useState<MastheadVariant>('instrument');
  const [tier, setTier] = useState<DisclosureTier>('restricted');

  const Masthead =
    variant === 'banner' ? MastheadBanner : variant === 'split' ? MastheadSplit : MastheadInstrument;

  return (
    <Screen>
      <ScreenHeader
        eyebrow={'§ Supplier record · ' + hero.recordId}
        title="Qualification record"
        actions={
          <>
            <Button sm variant="ghost" icon="refresh">
              Re-verify
            </Button>
            <Button sm variant="secondary" icon="download">
              Export
            </Button>
            <Button sm variant="secondary" icon="share">
              Share
            </Button>
          </>
        }
      />

      {/* layout variant + disclosure tier */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            masthead
          </span>
          <SegmentedControl
            value={variant}
            onChange={setVariant}
            ariaLabel="masthead"
            options={[
              { v: 'banner', label: 'banner' },
              { v: 'instrument', label: 'instrument' },
              { v: 'split', label: 'split' },
            ]}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            disclosure
          </span>
          <SegmentedControl
            value={tier}
            onChange={setTier}
            ariaLabel="disclosure"
            options={[
              { v: 'public', label: 'public' },
              { v: 'restricted', label: 'restricted' },
              { v: 'authority', label: 'authority' },
            ]}
          />
        </label>
      </div>

      <div>
        <Masthead s={s} h={hero} c={c} />
      </div>

      {/* depth gradient */}
      <div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            marginBottom: 8,
          }}
        >
          Depth reached
        </div>
        <DepthGradient reached={toDepth(hero.depthReached)} />
      </div>

      {/* weakest link */}
      <div>
        <WeakestLinkBanner tone={hero.weakest.state === 'flag' ? 'flag' : 'attest'}>
          <b>{hero.weakest.checkId} · unit authenticity.</b> {hero.weakest.text}
        </WeakestLinkBanner>
      </div>

      {/* renewal summary */}
      <div className="panel" style={{ padding: '18px 20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            Next credential to lapse
          </span>
          <Button sm variant="ghost" icon="clock" onClick={() => go('renewals')}>
            Open in renewals
          </Button>
        </div>
        <RenewalTimeline
          name={s.nextLapse.name}
          meta={'expiring · ' + s.nextLapse.days + ' days'}
          state="expiring"
          pct={78}
        />
      </div>

      {/* diligence coverage */}
      <div>
        <Coverage checks={hero.checks} />
      </div>

      {/* per-check table */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            Per-check breakdown
          </span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
            {tier === 'public' ? 'evidence redacted' : 'click a row for evidence'}
          </span>
        </div>
        <CheckTable checks={hero.checks} tier={tier} />
      </div>
    </Screen>
  );
}
